import React, { Component } from 'react';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Pagination, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import ApiIcon from '@mui/icons-material/Api';
import TimelineIcon from '@mui/icons-material/Timeline';
import CloseIcon from '@mui/icons-material/Close';
import CustomizedSnackbars from '../../components/material-snackbars';
import UsageEventDrawer from '../../components/usage/usage-event-drawer';
import Api from '../../services/api';
import UsageService from '../../services/usage.service';
import UsagePeriodFilter, { presetRange } from '../../components/usage/usage-period-filter';
import { UsageEllipsisCell, UsageKpiCard, UsageApiChip, UsageKindChip, UsageStatusChip, UsageOriginChip, UsageEmpty, UsageBlockTitle, STICKY_CELL_CLASS, SELECTED_ROW_CLASS, fmtDateTime, fmtInt, fmtMs, kindLabel } from '../../components/usage/usage-widgets';
import { t } from '../../components/usage/usage-i18n';

// ---------------------------------------------------------------------------
// Aba "Endpoints": responde "quem mais chamou o endpoint X, de onde, e era gente ou robô?".
// Ranking de rotas pelo resumo diário (até 400 dias, busca "rota contém"; com filtro de IP, pelo resumo por
// IP); clique numa rota abre, do histórico detalhado (≤ 90 dias), quem mais chamou (usuários) e de onde (IPs),
// cada um com a CADÊNCIA: intervalos entre chamadas consecutivas em faixas (< 1 s, 1–5 s, 5–30 s, 30 s–5 min,
// > 5 min), mediana, menor intervalo e pico por minuto. O chip humano / provável robô / robô é uma leitura
// desses números, que ficam ao lado para o analista julgar. Clique num usuário ou IP: painel à direita
// (empurra a página, como no Tráfego por IP) com as chamadas dele nessa rota, coluna Δ e drawer de detalhe.
// O botão "linha do tempo" leva à visão completa já filtrada pela rota.
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;
const CALLERS_LIMIT = 25;
const REQ_PAGE_SIZE = 20;
const KINDS = ['', 'read', 'write', 'export', 'denied', 'permission_denied', 'role_denied', 'blocked', 'rate_limited'];
const BUCKETS = [
  { key: 'lt1', color: '#c62828', label: '< 1 s' }, { key: 'lt5', color: '#ef6c00', label: '1–5 s' }, { key: 'lt30', color: '#f9a825', label: '5–30 s' },
  { key: 'lt300', color: '#7cb342', label: '30 s – 5 min' }, { key: 'gt300', color: '#2e7d32', label: '> 5 min' },
];

// Leitura da cadência. Regras simples e visíveis: robô = dezenas de chamadas com mediana abaixo de 2 s;
// provável robô = pico acima do que um humano faz num minuto, ou centenas de chamadas com mediana curta,
// ou 80% dos intervalos abaixo de 5 s; poucas chamadas não dão para classificar.
export function classifyCadence(r) {
  const calls = Number(r.calls) || 0;
  const intervals = Math.max(0, calls - 1);
  const median = r.medianDelta === null || r.medianDelta === undefined ? null : Number(r.medianDelta);
  const fast = (Number(r.lt1) || 0) + (Number(r.lt5) || 0);
  if (calls < 5) return 'few';
  if (calls >= 30 && median !== null && median < 2) return 'robot';
  if ((Number(r.peakPerMinute) || 0) >= 30 || (calls >= 100 && median !== null && median < 5) || (calls >= 30 && intervals > 0 && fast / intervals >= 0.8)) return 'probable';
  return 'human';
}

const CLASS_STYLE = {
  robot: { bg: '#c62828', fg: '#fff', id: 'usage.ep.class.robot', fallback: 'robô' },
  probable: { bg: '#ef6c00', fg: '#fff', id: 'usage.ep.class.probable', fallback: 'provável robô' },
  human: { bg: '#2e7d32', fg: '#fff', id: 'usage.ep.class.human', fallback: 'humano' },
  few: { bg: '#e0e0e0', fg: '#555', id: 'usage.ep.class.few', fallback: 'poucas chamadas' },
};

const fmtSec = (s) => (s === null || s === undefined ? '—' : Number(s) < 1 ? `${Math.round(Number(s) * 1000)} ms` : Number(s) < 60 ? `${Number(s).toFixed(1)} s` : Number(s) < 3600 ? `${(Number(s) / 60).toFixed(1)} min` : `${(Number(s) / 3600).toFixed(1)} h`);
// Intervalo desde a chamada anterior (linha de baixo). Mediana 0 ms = chamadas em paralelo (várias no mesmo milissegundo).
const fmtDelta = (ms) => (ms === null || ms === undefined ? '—' : ms < 1000 ? `${ms} ms` : ms < 60000 ? `${(ms / 1000).toFixed(1)} s` : ms < 3600000 ? `${Math.round(ms / 60000)} min` : `${(ms / 3600000).toFixed(1)} h`);

// Barrinha empilhada das faixas de intervalo + chip da leitura + números (mediana, mínimo, pico/min).
export function UsageCadence({ intl, row }) {
  const total = BUCKETS.reduce((a, b) => a + (Number(row[b.key]) || 0), 0);
  const cls = classifyCadence(row);
  const st = CLASS_STYLE[cls];
  const detail = `${t(intl, 'usage.ep.cadence.median', 'mediana')} ${fmtSec(row.medianDelta)} · ${t(intl, 'usage.ep.cadence.min', 'mín.')} ${fmtSec(row.minDelta)} · ${t(intl, 'usage.ep.cadence.peak', 'pico')} ${fmtInt(row.peakPerMinute)}/min`;
  const legend = BUCKETS.map((b) => `${b.label}: ${fmtInt(row[b.key])}`).join(' · ') + (Number(row.medianDelta) === 0 ? ` · ${t(intl, 'usage.ep.cadence.parallel', 'mediana 0 ms = chamadas em paralelo, várias no mesmo milissegundo')}` : '');
  return (
    <Box style={{ minWidth: 220 }}>
      <Box display="flex" alignItems="center" style={{ gap: 6 }}>
        <Chip size="small" label={t(intl, st.id, st.fallback)} style={{ backgroundColor: st.bg, color: st.fg, fontWeight: 600, height: 20, fontSize: 11 }} />
        <Typography variant="caption" style={{ color: '#666', whiteSpace: 'nowrap' }}>{detail}</Typography>
      </Box>
      <Tooltip title={legend}>
        <Box display="flex" style={{ height: 8, borderRadius: 4, overflow: 'hidden', background: '#f0f0f0', marginTop: 4 }}>
          {total > 0 && BUCKETS.map((b) => { const v = Number(row[b.key]) || 0; return v > 0 ? <Box key={b.key} style={{ width: `${(v / total) * 100}%`, background: b.color }} /> : null; })}
        </Box>
      </Tooltip>
    </Box>
  );
}

const noRequests = () => ({ open: false, loading: false, rows: [], count: 0, page: 1, userId: 0, userName: '', ip: '' });

export default class UsageEndpoints extends Component {
  constructor(props) {
    super(props);
    const q = new URLSearchParams(props.location?.search || '');
    const start = q.get('start'); const end = q.get('end');
    const range = start && end
      ? { preset: 'custom', dateStart: moment(start).startOf('day').format('YYYY/MM/DD HH:mm:ss'), dateEnd: moment(end).endOf('day').format('YYYY/MM/DD HH:mm:ss') }
      : { preset: '7d', ...presetRange('7d') };
    this.state = {
      filter: { ...range, api: '', actionGroup: '', userId: Number(q.get('userId')) || 0, userName: q.get('userName') || '', ip: props.ip || '' },
      search: q.get('route') || '', actionKind: '',
      loading: false, loaded: false, rows: [], count: 0, page: 1,
      sort: { key: 'requests', dir: 'desc' },
      selected: null, callers: { byUser: [], byIp: [] }, callersLoading: false,
      requests: noRequests(), drawerRow: null,
      snack: { open: false, variant: 'error', message: '' },
    };
    this.onFilter = this.onFilter.bind(this);
    this.load = this.load.bind(this);
    this.onPage = this.onPage.bind(this);
    this.select = this.select.bind(this);
    this.closeSnack = this.closeSnack.bind(this);
    this.onRequestsPage = this.onRequestsPage.bind(this);
    this.closeRequests = this.closeRequests.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
  }

  componentDidMount() { this.lastSearch = this.props.location?.search || ''; this.load(1); }

  // IP vem da área (index.js): mudou em outra aba → aplica aqui e recarrega o que já estava carregado.
  componentDidUpdate(prevProps) {
    if ((prevProps.ip || '') !== (this.props.ip || '') && (this.props.ip || '') !== (this.state.filter.ip || '')) {
      this.setState((s) => ({ filter: { ...s.filter, ip: this.props.ip || '' } }), () => { if (this.state.loaded) this.load(1); });
    }
  }

  tr(id, fallback) { return t(this.props.intl, id, fallback); }
  onFilter(next) {
    if ((next.ip || '') !== (this.state.filter.ip || '') && this.props.onIpChange) this.props.onIpChange(next.ip || '');
    this.setState({ filter: next });
  }
  onPage(e, page) { this.load(page); }
  closeSnack() { this.setState({ snack: { ...this.state.snack, open: false } }); }
  notify(variant, message) { this.setState({ snack: { open: true, variant, message } }); }
  sortBy(key) { this.setState((s) => ({ sort: { key, dir: s.sort.key === key && s.sort.dir === 'desc' ? 'asc' : 'desc' } })); }
  days() { const f = this.state.filter; return moment(f.dateEnd).diff(moment(f.dateStart), 'days') + 1; }

  load(page) {
    const f = this.state.filter;
    this.setState({ loading: true, page, selected: null, callers: { byUser: [], byIp: [] }, requests: noRequests() });
    UsageService.endpointsRanking(f, this.state.search.trim(), this.state.actionKind, page, PAGE_SIZE)
      .then((r) => this.setState({ loading: false, loaded: true, rows: r.data, count: r.count }))
      .catch((err) => { Api.kickoff(err); this.setState({ loading: false }); this.notify('error', err.message); });
  }

  // Abre o detalhe da rota (quem chamou / de onde, com cadência). Clicar de novo fecha.
  select(row) {
    const same = this.state.selected && this.state.selected.route === row.route && this.state.selected.api === row.api;
    if (same) { this.setState({ selected: null, callers: { byUser: [], byIp: [] }, requests: noRequests() }); return; }
    this.setState({ selected: row, callers: { byUser: [], byIp: [] }, callersLoading: true, requests: noRequests() });
    UsageService.endpointCallers(row.route, row.api, this.state.actionKind, this.state.filter, CALLERS_LIMIT)
      .then((callers) => this.setState({ callers, callersLoading: false }))
      .catch((err) => { Api.kickoff(err); this.setState({ callersLoading: false }); this.notify('error', err.message); });
  }

  // Painel lateral: chamadas de UM usuário (ou de UM IP) na rota selecionada — a linha do tempo do fato
  // recortada por rota, sem sair da aba. Clicar de novo na mesma linha fecha.
  openRequests(userId, userName, ip) {
    const r = this.state.requests;
    if (r.open && r.userId === (userId || 0) && r.ip === (ip || '')) { this.closeRequests(); return; }
    this.setState({ requests: { ...noRequests(), open: true, loading: true, userId: userId || 0, userName: userName || '', ip: ip || '' } }, () => this.loadRequests(1));
  }

  loadRequests(page) {
    const f = this.state.filter; const s = this.state.selected; const r = this.state.requests;
    if (!s) return;
    this.setState((st) => ({ requests: { ...st.requests, loading: true, page } }));
    UsageService.activity({ userId: r.userId || 0, dateStart: f.dateStart, dateEnd: f.dateEnd, page, pageSize: REQ_PAGE_SIZE, ip: r.ip || (r.userId ? f.ip : '') || undefined, api: s.api || undefined, route: s.route || undefined, actionKind: this.state.actionKind || undefined })
      .then((res) => this.setState((st) => ({ requests: { ...st.requests, loading: false, rows: res.data, count: res.count, page } })))
      .catch((err) => { Api.kickoff(err); this.setState((st) => ({ requests: { ...st.requests, loading: false } })); this.notify('error', err.message); });
  }
  onRequestsPage(e, page) { this.loadRequests(page); }
  closeRequests() { this.setState({ requests: noRequests() }); }
  closeDrawer() { this.setState({ drawerRow: null }); }

  // Linha do tempo do usuário (ou do IP) já filtrada pela rota selecionada.
  goTimeline(userId, userName, ip) {
    const f = this.state.filter; const s = this.state.selected;
    if (ip && this.props.onIpChange) this.props.onIpChange(ip);
    const params = `start=${moment(f.dateStart).format('YYYY-MM-DD')}&end=${moment(f.dateEnd).format('YYYY-MM-DD')}&route=${encodeURIComponent(s ? s.route : '')}`
      + (userId ? `&userId=${userId}&userName=${encodeURIComponent(userName || '')}` : '') + (ip ? `&ip=${encodeURIComponent(ip)}` : '');
    this.props.history.push(`/${this.props.lang}/usage/user-activity?${params}`);
  }

  renderRanking() {
    const { intl } = this.props;
    const { rows, sort, selected, filter } = this.state;
    if (!rows.length) return <UsageEmpty intl={intl} compact title={this.tr('usage.ep.none', 'Nenhum endpoint no período com esses filtros')} />;
    const byIp = !!filter.ip; // ranking pelo resumo por IP: sem usuários nem durações
    const cols = [
      ['api', 'API', 'left'], ['route', this.tr('usage.col.route', 'Rota'), 'left'], ['requests', this.tr('usage.ipt.col.requests', 'Requests'), 'right'],
      ['users', this.tr('usage.col.users', 'Usuários'), 'right'], ['denials', this.tr('usage.col.denials', 'Recusas'), 'right'], ['errors', this.tr('usage.col.errors', 'Erros 5xx'), 'right'],
      ['clientErrors', this.tr('usage.ep.col.clientErrors', 'Erros 4xx'), 'right'], ['avgDurationMs', this.tr('usage.col.avg', 'Média'), 'right'], ['maxDurationMs', this.tr('usage.screens.col.max', 'Máx'), 'right'],
      ['lastDay', this.tr('usage.screens.col.lastDay', 'Último uso'), 'left'],
    ];
    const sorted = [...rows].sort((a, b) => {
      const va = a[sort.key]; const vb = b[sort.key];
      const r = typeof va === 'string' ? String(va).localeCompare(String(vb)) : (Number(va) || 0) - (Number(vb) || 0);
      return sort.dir === 'asc' ? r : -r;
    });
    const dash = <span style={{ color: '#bbb' }}>—</span>;
    return (
      <Box style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            {cols.map(([k, l, al]) => <TableCell key={k} align={al}><TableSortLabel active={sort.key === k} direction={sort.key === k ? sort.dir : 'desc'} onClick={() => this.sortBy(k)}>{l}</TableSortLabel></TableCell>)}
            <TableCell className={STICKY_CELL_CLASS} />
          </TableRow></TableHead>
          <TableBody>
            {sorted.map((r) => {
              const isSel = !!selected && selected.route === r.route && selected.api === r.api;
              return (
                <TableRow key={`${r.api}|${r.route}`} hover selected={isSel} className={isSel ? SELECTED_ROW_CLASS : undefined} style={{ cursor: 'pointer' }} onClick={() => this.select(r)}>
                  <TableCell><UsageApiChip api={r.api} /></TableCell>
                  <TableCell><UsageEllipsisCell text={r.route} maxWidth={340} mono bold={isSel} /></TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}><b>{fmtInt(r.requests)}</b></TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{byIp ? dash : fmtInt(r.users)}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: r.denials ? '#c62828' : undefined }}>{fmtInt(r.denials)}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: r.errors ? '#c62828' : undefined }}>{fmtInt(r.errors)}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtInt(r.clientErrors)}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{byIp ? dash : fmtMs(r.avgDurationMs)}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{byIp ? dash : fmtMs(r.maxDurationMs)}</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap', color: '#666' }}>{r.lastDay ? moment(r.lastDay).format('DD/MM/YYYY') : '-'}</TableCell>
                  <TableCell className={STICKY_CELL_CLASS}>
                    <Button size="small" startIcon={<TimelineIcon />} onClick={(e) => { e.stopPropagation(); this.setState({ selected: r }, () => this.goTimeline(filter.userId, filter.userName, filter.ip)); }}>{this.tr('usage.ipt.timeline', 'linha do tempo')}</Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  }

  renderCallers(list, byIp) {
    const { intl } = this.props;
    const { requests } = this.state;
    const anon = this.tr('usage.anonymous', 'anônimo');
    if (!list.length) return <UsageEmpty intl={intl} compact title={this.tr('usage.ep.callers.none', 'Nada no histórico detalhado para esta rota no período')} />;
    return (
      <Box style={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell>{byIp ? 'IP' : this.tr('usage.col.user', 'Usuário')}</TableCell>
            <TableCell align="right">{this.tr('usage.ep.col.calls', 'Chamadas')}</TableCell>
            <TableCell align="right">{byIp ? this.tr('usage.col.users', 'Usuários') : 'IPs'}</TableCell>
            <TableCell align="right">{this.tr('usage.col.denials', 'Recusas')}</TableCell>
            <TableCell>{this.tr('usage.ep.col.cadence', 'Cadência (intervalo entre chamadas)')}</TableCell>
            <TableCell>{this.tr('usage.col.first', 'Primeiro')}</TableCell><TableCell>{this.tr('usage.col.last', 'Último')}</TableCell>
            <TableCell className={STICKY_CELL_CLASS} />
          </TableRow></TableHead>
          <TableBody>
            {list.map((r) => {
              const isSel = requests.open && (byIp ? requests.ip === r.ip && !requests.userId : requests.userId === r.userId && !requests.ip);
              return (
                <TableRow key={byIp ? r.ip : r.userId} hover selected={isSel} className={isSel ? SELECTED_ROW_CLASS : undefined} style={{ cursor: 'pointer' }}
                  onClick={() => (byIp ? this.openRequests(0, '', r.ip) : this.openRequests(r.userId, r.userName, ''))}>
                  <TableCell>{byIp
                    ? <span style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.ip || '—'}</span>
                    : <UsageEllipsisCell text={r.userId ? (r.userName || `#${r.userId}`) : anon} title={r.userId ? `#${r.userId}` : ''} maxWidth={200} />}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}><b>{fmtInt(r.calls)}</b></TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums' }}>{byIp ? fmtInt(r.users) : <Tooltip title={r.ips || ''}><span>{fmtInt(r.ipCount)}</span></Tooltip>}</TableCell>
                  <TableCell align="right" style={{ fontVariantNumeric: 'tabular-nums', color: r.denials ? '#c62828' : undefined }}>{fmtInt(r.denials)}</TableCell>
                  <TableCell><UsageCadence intl={intl} row={r} /></TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.firstAt)}</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(r.lastAt)}</TableCell>
                  <TableCell className={STICKY_CELL_CLASS}>
                    {(byIp ? r.ip : r.userId) ? <Button size="small" startIcon={<TimelineIcon />} onClick={(e) => { e.stopPropagation(); if (byIp) this.goTimeline(0, '', r.ip); else this.goTimeline(r.userId, r.userName, ''); }}>{this.tr('usage.ipt.timeline', 'linha do tempo')}</Button> : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  }

  renderDetail() {
    const { intl } = this.props;
    const { selected, callers, callersLoading } = this.state;
    if (!selected) return null;
    const tooLong = this.days() > 90;
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, borderLeft: '4px solid #3F51B4', marginTop: 12 }}><CardContent>
        <UsageBlockTitle title={<span><UsageApiChip api={selected.api} /> <span style={{ fontFamily: 'monospace' }}>{selected.route}</span></span>}
          subtitle={this.tr('usage.ep.detail.subtitle', 'Histórico detalhado do período (até 90 dias): quem chamou, de onde, e a cadência. Clique numa linha para ver as chamadas dela ao lado; "linha do tempo" abre a visão completa.')}
          right={<Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Button size="small" startIcon={<TimelineIcon />} onClick={() => this.goTimeline(0, '', '')}>{this.tr('usage.ep.allCalls', 'todas as chamadas')}</Button>
            <Button size="small" startIcon={<CloseIcon />} onClick={() => this.select(selected)}>{this.tr('usage.ipt.close', 'fechar')}</Button>
          </Box>} />
        <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: 8, marginBottom: 8 }}>
          {BUCKETS.map((b) => <Box key={b.key} display="flex" alignItems="center" style={{ gap: 4 }}><Box style={{ width: 10, height: 10, borderRadius: 2, background: b.color }} /><Typography variant="caption" style={{ color: '#666' }}>{b.label}</Typography></Box>)}
          <Typography variant="caption" style={{ color: '#888' }}>· {this.tr('usage.ep.cadence.hint', 'robô: dezenas de chamadas com mediana abaixo de 2 s · provável robô: pico de 30+/min, ou centenas com mediana abaixo de 5 s, ou 80% dos intervalos abaixo de 5 s · mediana 0 ms: chamadas em paralelo')}</Typography>
        </Box>
        {tooLong && <UsageEmpty intl={intl} compact title={this.tr('usage.ep.tooLong', 'Período acima de 90 dias: reduza o período para ver quem chamou (leitura do histórico detalhado).')} />}
        {!tooLong && callersLoading && <Box display="flex" justifyContent="center" style={{ padding: 8 }}><CircularProgress size={22} color="secondary" /></Box>}
        {!tooLong && !callersLoading && (
          <Grid container spacing={2}>
            <Grid item xs={12} xl={6}>
              <UsageBlockTitle title={this.tr('usage.ep.byUser.title', 'Quem mais chamou')} subtitle={this.tr('usage.ep.byUser.subtitle', 'por usuário · cadência calculada nas chamadas do próprio usuário')} />
              {this.renderCallers(callers.byUser, false)}
            </Grid>
            <Grid item xs={12} xl={6}>
              <UsageBlockTitle title={this.tr('usage.ep.byIp.title', 'De onde')} subtitle={this.tr('usage.ep.byIp.subtitle', 'por IP · cadência calculada nas chamadas do próprio IP')} />
              {this.renderCallers(callers.byIp, true)}
            </Grid>
          </Grid>
        )}
      </CardContent></Card>
    );
  }

  // Painel lateral: chamadas do usuário/IP na rota selecionada, com Δ (intervalo desde a linha de baixo).
  renderRequests() {
    const { intl } = this.props;
    const { selected, requests } = this.state;
    const anon = this.tr('usage.anonymous', 'anônimo');
    const who = requests.ip ? <span style={{ fontFamily: 'monospace' }}>{requests.ip}</span> : (requests.userName || (requests.userId ? `#${requests.userId}` : anon));
    return (
      <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, borderLeft: '4px solid #277afe' }}><CardContent style={{ paddingBottom: 8 }}>
        <UsageBlockTitle title={<span>{this.tr('usage.ipt.req.title', 'Requisições')} · {who}</span>}
          subtitle={<span><UsageApiChip api={selected ? selected.api : ''} /> <span style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>{selected ? selected.route : ''}</span> · {fmtInt(requests.count)} {this.tr('usage.ipt.req.subtitle', 'no período · clique na linha para ver o detalhe')}</span>}
          right={<Box display="flex" alignItems="center" style={{ gap: 4 }}>
            <Button size="small" startIcon={<TimelineIcon />} onClick={() => this.goTimeline(requests.userId, requests.userName, requests.ip)}>{this.tr('usage.ipt.timeline', 'linha do tempo')}</Button>
            <Button size="small" startIcon={<CloseIcon />} onClick={this.closeRequests}>{this.tr('usage.ipt.close', 'fechar')}</Button>
          </Box>} />
        {requests.loading && <Box display="flex" justifyContent="center" style={{ padding: 8 }}><CircularProgress size={22} color="secondary" /></Box>}
        {!requests.loading && !requests.rows.length && <UsageEmpty intl={intl} compact title={this.tr('usage.ipt.req.none', 'Nenhuma requisição desse endpoint no período')} />}
        {!requests.loading && requests.rows.length > 0 && (
          <Box style={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>{this.tr('usage.col.when', 'Quando')}</TableCell><TableCell align="right">{this.tr('usage.col.delta', 'Δ anterior')}</TableCell>
                {requests.ip ? <TableCell>{this.tr('usage.col.user', 'Usuário')}</TableCell> : <TableCell>IP</TableCell>}
                <TableCell>{this.tr('usage.col.action', 'Ação')}</TableCell><TableCell>Status</TableCell><TableCell align="right">{this.tr('usage.col.duration', 'Duração')}</TableCell><TableCell>{this.tr('usage.col.origin', 'Origem')}</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {requests.rows.map((r, i) => {
                  const prev = requests.rows[i + 1];
                  const delta = prev ? Math.max(0, new Date(r.occurredAt) - new Date(prev.occurredAt)) : null;
                  return (
                    <TableRow key={r.id} hover style={{ cursor: 'pointer' }} onClick={() => this.setState({ drawerRow: r })}>
                      <TableCell style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtDateTime(r.occurredAt, true)}</TableCell>
                      <TableCell align="right" style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', color: delta !== null && delta < 1000 ? '#c62828' : delta !== null && delta < 5000 ? '#ef6c00' : '#666' }}>{fmtDelta(delta)}</TableCell>
                      {requests.ip ? <TableCell><UsageEllipsisCell text={r.userName || (r.userId ? `#${r.userId}` : anon)} maxWidth={140} /></TableCell> : <TableCell style={{ whiteSpace: 'nowrap' }}>{r.ip}</TableCell>}
                      <TableCell><UsageKindChip intl={intl} kind={r.actionKind} details={r.details} /></TableCell>
                      <TableCell><UsageStatusChip code={r.statusCode} /></TableCell>
                      <TableCell align="right" style={{ whiteSpace: 'nowrap' }}>{fmtMs(r.durationMs)}</TableCell>
                      <TableCell><UsageOriginChip intl={intl} row={r} maxWidth={140} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        )}
        {requests.count > REQ_PAGE_SIZE && <Box style={{ marginTop: 8 }}><Pagination size="small" count={Math.ceil(requests.count / REQ_PAGE_SIZE)} page={requests.page} onChange={this.onRequestsPage} /></Box>}
      </CardContent></Card>
    );
  }

  render() {
    const { intl } = this.props;
    const { filter, search, actionKind, loading, loaded, rows, count, page, snack, requests, drawerRow } = this.state;
    const extra = (
      <>
        <Grid item xs={12} sm={6} md={3}>
          <TextField fullWidth size="small" label={this.tr('usage.ua.route', 'Rota / endpoint (contém)')} placeholder="GetFileDownloadLink" value={search} InputLabelProps={{ shrink: true }}
            onChange={(e) => this.setState({ search: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') this.load(1); }} />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <TextField select fullWidth size="small" label={this.tr('usage.ua.action', 'Ação')} value={actionKind} InputLabelProps={{ shrink: true }} SelectProps={{ native: true }} onChange={(e) => this.setState({ actionKind: e.target.value })}>
            {KINDS.map((k) => <option key={k} value={k}>{k ? kindLabel(intl, k) : this.tr('usage.ua.action.all', 'Todas')}</option>)}
          </TextField>
        </Grid>
      </>
    );
    const totalRequests = rows.reduce((a, r) => a + (Number(r.requests) || 0), 0);
    return (
      <Box className="usage-ipt-layout">
      <Box className="usage-ipt-main">
        <UsagePeriodFilter intl={intl} value={filter} onChange={this.onFilter} showGroup={false} extra={extra} onApply={() => this.load(1)} applying={loading} />
        <Typography variant="caption" style={{ color: '#888' }}>{filter.ip
          ? this.tr('usage.ep.hint.ip', 'Com IP: o ranking vem do resumo por IP (dia/hora/API/rota), que não guarda usuários nem durações. Quem chamou e a cadência vêm do histórico detalhado, só em períodos de até 90 dias.')
          : this.tr('usage.ep.hint', 'Fonte do ranking: resumo diário por rota (até 400 dias), refeito de hora em hora. Quem chamou e a cadência vêm do histórico detalhado, só em períodos de até 90 dias.')}</Typography>
        {loading && <Box display="flex" justifyContent="center" style={{ padding: 16 }}><CircularProgress color="secondary" /></Box>}
        {!loading && loaded && (
          <Box style={{ marginTop: 16 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ep.kpi.listed', 'Endpoints listados')} value={count} color="#3F51B4" /></Grid>
              <Grid item xs={6} md={3}><UsageKpiCard intl={intl} title={this.tr('usage.ep.kpi.requestsPage', 'Requests (nesta página)')} value={totalRequests} color="#607D8B" /></Grid>
            </Grid>
            {count === 0 ? <Box style={{ marginTop: 16 }}><UsageEmpty intl={intl} icon={<ApiIcon style={{ fontSize: 56 }} />} title={this.tr('usage.ep.empty.title', 'Nenhum endpoint no período')} description={this.tr('usage.ep.empty.description', 'O resumo é refeito de hora em hora. Se o período é recente, use "Atualizar agora" no Dashboard.')} /></Box> : (
              <Box>
                <Card style={{ border: '1px solid #e0e0e0', borderRadius: 12, marginTop: 12 }}><CardContent style={{ paddingBottom: 8 }}>
                  <UsageBlockTitle title={this.tr('usage.ep.ranking.title', 'Endpoints por volume')} subtitle={this.tr('usage.ep.ranking.subtitle', 'Clique numa rota para ver quem chamou, de onde e a cadência')} />
                  {this.renderRanking()}
                  {count > PAGE_SIZE && <Box style={{ marginTop: 8 }}><Pagination count={Math.ceil(count / PAGE_SIZE)} page={page} onChange={this.onPage} /></Box>}
                </CardContent></Card>
                {this.renderDetail()}
              </Box>
            )}
          </Box>
        )}
        <UsageEventDrawer intl={intl} open={!!drawerRow} row={drawerRow} onClose={this.closeDrawer} lang={this.props.lang} history={this.props.history} onCopy={() => this.notify('success', this.tr('usage.copied', 'Copiado!'))} />
        <CustomizedSnackbars variant={snack.variant} message={snack.message} isOpen={snack.open} toClose={this.closeSnack} />
      </Box>
      {requests.open && <Box className="usage-ipt-side">{this.renderRequests()}</Box>}
      </Box>
    );
  }
}
